import type { AccreditationKind, AccreditationRequest } from "@/services/types"
import { onlyDigits } from "@/shared/lib/documents"

/* ─────────────────────────────────────────────────────────────────────────────
 * A ficha de credenciamento como dado.
 *
 * Opcoes, estado inicial e a traducao para o modelo de dominio vivem aqui, e
 * nao no JSX. A ficha tem quase quarenta campos: escrita como marcacao, uma
 * mudanca de rotulo vira uma cacada num arquivo de mil linhas — e foi
 * exatamente o que aconteceu com `PlansSection` no prototipo.
 *
 * O estado do formulario e PLANO, enquanto `AccreditationRequest` e aninhado
 * por bloco. E deliberado: campo plano casa 1:1 com `id` de input e com o mapa
 * de erros, e `toRequest` faz a costura num lugar so, na saida.
 * ────────────────────────────────────────────────────────────────────────── */

export interface Option {
  id: string
  label: string
}

export const ACCREDITATION_KINDS: ReadonlyArray<{ id: AccreditationKind; label: string }> = [
  { id: "clinica", label: "Clínica Veterinária" },
  { id: "consultorio", label: "Consultório Veterinário" },
  { id: "hospital", label: "Hospital Veterinário" },
  { id: "autonomo", label: "Médico-Veterinário Autônomo" },
]

export const ACCREDITATION_SERVICES: ReadonlyArray<Option> = [
  { id: "consultas", label: "Consultas" },
  { id: "urgencia", label: "Atendimento de urgência" },
  { id: "internacao", label: "Internação" },
  { id: "cirurgias", label: "Cirurgias" },
  { id: "anestesia", label: "Anestesia" },
  { id: "vacinacao", label: "Vacinação" },
  { id: "exames-laboratoriais", label: "Exames laboratoriais" },
  { id: "ultrassonografia", label: "Ultrassonografia" },
  { id: "radiografia", label: "Radiografia" },
  { id: "cardiologia", label: "Cardiologia" },
  { id: "dermatologia", label: "Dermatologia" },
  { id: "odontologia", label: "Odontologia" },
  { id: "ortopedia", label: "Ortopedia" },
  { id: "oftalmologia", label: "Oftalmologia" },
]

export const ACCREDITATION_DOCUMENTS: ReadonlyArray<Option> = [
  { id: "cartao-cnpj-cpf", label: "Cartão CNPJ / CPF" },
  { id: "registro-crmv", label: "Registro CRMV" },
  { id: "documento-responsavel", label: "Documento do responsável" },
  { id: "comprovante-endereco", label: "Comprovante de endereço" },
  { id: "alvara-funcionamento", label: "Alvará de funcionamento" },
  { id: "licenca-sanitaria", label: "Licença sanitária, quando aplicável" },
  { id: "comprovante-bancario", label: "Comprovante de dados bancários" },
]

export const ACCOUNT_KINDS: ReadonlyArray<Option> = [
  { id: "corrente", label: "Conta corrente" },
  { id: "poupanca", label: "Conta poupança" },
  { id: "pagamentos", label: "Conta de pagamentos" },
]

/** Marca a caixa "Outros" dos servicos e da documentacao. */
export const OTHER_OPTION_ID = "outros"

export type YesNo = "sim" | "nao"

export interface AccreditationFormValues {
  /* 1. Tipo de credenciamento */
  kind: AccreditationKind | ""

  /* 2. Dados do estabelecimento / profissional */
  legalName: string
  tradeName: string
  taxId: string
  crmv: string
  address: string
  district: string
  city: string
  state: string
  postalCode: string
  phone: string
  whatsapp: string
  email: string
  socialMedia: string

  /* 3. Responsavel tecnico */
  managerName: string
  managerCrmv: string
  managerCpf: string
  managerPhone: string
  managerEmail: string

  /* 4. Servicos disponiveis */
  services: string[]
  otherServicesChecked: boolean
  otherServices: string

  /* 5. Horario de funcionamento */
  openingHours: string
  open24h: YesNo | ""

  /* 6. Condicoes de atendimento */
  negotiatedPrices: string
  paymentTerms: string
  notes: string

  /* 7. Dados bancarios para repasse */
  bank: string
  bankBranch: string
  bankAccount: string
  bankAccountKind: string
  bankHolder: string
  bankHolderTaxId: string
  pixKey: string

  /* 8. Documentacao */
  documents: string[]
  otherDocumentsChecked: boolean
  otherDocuments: string

  /* 9. Declaracao */
  declarationPlace: string
  declarationAccepted: boolean
}

export const EMPTY_VALUES: AccreditationFormValues = {
  kind: "",

  legalName: "",
  tradeName: "",
  taxId: "",
  crmv: "",
  address: "",
  district: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
  whatsapp: "",
  email: "",
  socialMedia: "",

  managerName: "",
  managerCrmv: "",
  managerCpf: "",
  managerPhone: "",
  managerEmail: "",

  services: [],
  otherServicesChecked: false,
  otherServices: "",

  openingHours: "",
  open24h: "",

  negotiatedPrices: "",
  paymentTerms: "",
  notes: "",

  bank: "",
  bankBranch: "",
  bankAccount: "",
  bankAccountKind: "",
  bankHolder: "",
  bankHolderTaxId: "",
  pixKey: "",

  documents: [],
  otherDocumentsChecked: false,
  otherDocuments: "",

  declarationPlace: "",
  declarationAccepted: false,
}

/** Liga/desliga um id numa lista de selecao multipla, preservando a ordem. */
export function toggleId(list: string[], id: string, checked: boolean): string[] {
  if (checked) return list.includes(id) ? list : [...list, id]
  return list.filter((item) => item !== id)
}

/**
 * Estado do formulario -> ficha enviada.
 *
 * Aqui a mascara sai: `taxId`, CPF, CEP e telefone viajam so com digitos. Quem
 * recebe do outro lado nao deveria ter que adivinhar se o ponto do CNPJ veio
 * porque o campo tinha mascara ou porque alguem digitou.
 *
 * `date` e a data local do navegador, e nao um `receivedAt`: e a data que o
 * candidato declara, o equivalente ao "Local e Data" da ficha em papel. A hora
 * de recebimento quem carimba e o destino.
 */
export function toRequest(values: AccreditationFormValues, today: string): AccreditationRequest {
  return {
    kind: values.kind as AccreditationKind,

    establishment: {
      legalName: values.legalName.trim(),
      tradeName: values.tradeName.trim(),
      taxId: onlyDigits(values.taxId),
      crmv: values.crmv.trim(),
      address: values.address.trim(),
      district: values.district.trim(),
      city: values.city.trim(),
      state: values.state,
      postalCode: onlyDigits(values.postalCode),
      phone: onlyDigits(values.phone),
      whatsapp: onlyDigits(values.whatsapp),
      email: values.email.trim(),
      socialMedia: values.socialMedia.trim(),
    },

    services: values.services,
    otherServices: values.otherServicesChecked ? values.otherServices.trim() : "",

    schedule: {
      hours: values.openingHours.trim(),
      open24h: values.open24h === "sim",
    },

    technicalManager: {
      name: values.managerName.trim(),
      crmv: values.managerCrmv.trim(),
      cpf: onlyDigits(values.managerCpf),
      phone: onlyDigits(values.managerPhone),
      email: values.managerEmail.trim(),
    },

    terms: {
      negotiatedPrices: values.negotiatedPrices.trim(),
      paymentTerms: values.paymentTerms.trim(),
      notes: values.notes.trim(),
    },

    bankAccount: {
      bank: values.bank.trim(),
      branch: values.bankBranch.trim(),
      account: values.bankAccount.trim(),
      accountKind: values.bankAccountKind,
      holder: values.bankHolder.trim(),
      holderTaxId: onlyDigits(values.bankHolderTaxId),
      pixKey: values.pixKey.trim(),
    },

    documents: values.documents,
    otherDocuments: values.otherDocumentsChecked ? values.otherDocuments.trim() : "",

    declaration: {
      place: values.declarationPlace.trim(),
      date: today,
      accepted: true,
    },
  }
}
